import React, { createContext, useContext, ReactNode } from 'react';

interface PlausibleContextType {
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
  trackGoal: (goalName: string, properties?: Record<string, any>) => void;
  trackOutboundLink: (url: string, properties?: Record<string, any>) => void;
  trackFileDownload: (fileName: string, properties?: Record<string, any>) => void;
}

const PlausibleContext = createContext<PlausibleContextType | undefined>(undefined);

interface PlausibleProviderProps {
  children: ReactNode;
}

export const PlausibleProvider: React.FC<PlausibleProviderProps> = ({ children }) => {
  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (window.plausible) {
      window.plausible(eventName, { props: properties });
    }
  };

  const trackGoal = (goalName: string, properties?: Record<string, any>) => {
    if (window.plausible) {
      window.plausible('goal', { props: { goal: goalName, ...properties } });
    }
  };

  const trackOutboundLink = (url: string, properties?: Record<string, any>) => {
    if (window.plausible) {
      window.plausible('outbound-link', { props: { url, ...properties } });
    }
  };

  const trackFileDownload = (fileName: string, properties?: Record<string, any>) => {
    if (window.plausible) {
      window.plausible('file-download', { props: { file: fileName, ...properties } });
    }
  };

  const value: PlausibleContextType = {
    trackEvent,
    trackGoal,
    trackOutboundLink,
    trackFileDownload,
  };

  return (
    <PlausibleContext.Provider value={value}>
      {children}
    </PlausibleContext.Provider>
  );
};

export const usePlausibleContext = (): PlausibleContextType => {
  const context = useContext(PlausibleContext);
  if (context === undefined) {
    throw new Error('usePlausibleContext must be used within a PlausibleProvider');
  }
  return context;
}; 